import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const API_BASE_URL = 'https://spaceportfolio-api.rajansudhan0.workers.dev';

const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fallbackTestimonials = [
      {
        id: 1,
        name: "Mr. Sanjay Pandit",
        position: "Mentor, NESAC UAV Training",
        company: "North Eastern Space Applications Centre",
        avatar: "https://nesac.gov.in/assets/resources/2020/11/nesac-logo-web.png",
        content: `Sudhan's UAV-based vegetation health project demonstrated a deep understanding of remote sensing indices and GIS tools. His commitment to precision and fieldwork analysis was commendable.`,
        rating: 5,
        project_title: "Vegetation Health Analysis using UAV Data",
        result_metric: "Accurate crop stress mapping using NDVI & NDRE"
      }
    ];

    fetch(`${API_BASE_URL}/api/testimonials`)
      .then(res => res.json())
      .then(data => {
        const safeData = Array.isArray(data) ? data : [];
        setTestimonials(safeData.length > 0 ? safeData : fallbackTestimonials);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching testimonials:', err);
        setTestimonials(fallbackTestimonials);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!isAutoPlaying || testimonials.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        testimonials.length > 0 ? (prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1) : 0
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === testimonials.length - 1 ? 0 : currentIndex + 1);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const currentTestimonial = (Array.isArray(testimonials) && testimonials.length > 0 && currentIndex < testimonials.length) 
    ? testimonials[currentIndex] 
    : null;

  // Define unique styles for different testimonials
  const cardStyles = [
    { bg: 'from-accent/10 to-transparent', border: 'border-accent/20 hover:border-accent/40', icon: 'text-accent', shadow: 'hover:shadow-accent/5' },
    { bg: 'from-purple-500/10 to-transparent', border: 'border-purple-500/20 hover:border-purple-500/40', icon: 'text-purple-400', shadow: 'hover:shadow-purple-500/5' },
    { bg: 'from-blue-500/10 to-transparent', border: 'border-blue-500/20 hover:border-blue-500/40', icon: 'text-blue-400', shadow: 'hover:shadow-blue-500/5' },
    { bg: 'from-green-500/10 to-transparent', border: 'border-green-500/20 hover:border-green-500/40', icon: 'text-green-400', shadow: 'hover:shadow-green-500/5' },
    { bg: 'from-orange-500/10 to-transparent', border: 'border-orange-500/20 hover:border-orange-500/40', icon: 'text-orange-400', shadow: 'hover:shadow-orange-500/5' },
    { bg: 'from-pink-500/10 to-transparent', border: 'border-pink-500/20 hover:border-pink-500/40', icon: 'text-pink-400', shadow: 'hover:shadow-pink-500/5' },
  ];

  const currentStyle = cardStyles[currentIndex % cardStyles.length];

  return (
    <section className="py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="font-heading font-bold text-3xl lg:text-4xl text-text-primary mb-4">
            What Experts & Mentors Say
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Insights from space scientists, educators, and mentors who’ve guided my journey across leading research and outreach programs.
          </p>
        </div>

        {/* Testimonial Card */}
        <div className="max-w-4xl mx-auto min-h-[400px]">
          {loading ? (
            <div className="h-64 animate-pulse bg-white/5 rounded-3xl"></div>
          ) : testimonials.length > 0 ? (
            <div className={`bg-surface/50 backdrop-blur-xl rounded-3xl p-8 lg:p-12 border ${currentStyle.border} shadow-2xl relative overflow-hidden transition-all duration-500 ease-in-out bg-gradient-to-br ${currentStyle.bg} ${currentStyle.shadow} min-h-[460px] md:min-h-[400px] lg:min-h-[380px] flex flex-col justify-between`}>
              {/* Background Pattern */}
              <div className={`absolute top-0 right-0 w-48 h-48 rounded-full -translate-y-24 translate-x-24 blur-3xl opacity-30 ${currentStyle.bg.split(' ')[0].replace('from-', 'bg-')}`}></div>
              
              {/* Quote Icon - positioned top right to avoid overlapping with rating stars */}
              <div className="absolute top-8 right-8 lg:top-12 lg:right-12 w-12 h-12 rounded-full flex items-center justify-center bg-white/5 border border-white/5 z-20">
                <Icon name="Quote" size={20} className={currentStyle.icon} />
              </div>

              <div className="relative flex flex-col justify-between flex-1 h-full">
                <div className="flex-1">
                  {/* Rating */}
                  <div className="flex items-center gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Icon key={i} name="Star" size={20} className="text-warning fill-current" />
                    ))}
                  </div>

                  {/* Testimonial Content */}
                  <blockquote className="text-lg lg:text-xl text-text-primary leading-relaxed mb-6 font-body">
                    "{currentTestimonial?.quote || currentTestimonial?.content}"
                  </blockquote>

                  {/* Project / Result Info */}
                  {(currentTestimonial?.project_title || currentTestimonial?.project) ? (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 p-4 bg-white/5 rounded-xl border border-white/5">
                      <div>
                        <div className="font-medium text-text-primary text-xs uppercase tracking-wider mb-1 opacity-70">Project</div>
                        <div className="text-text-secondary text-sm">{currentTestimonial?.project_title || currentTestimonial?.project}</div>
                      </div>
                      {(currentTestimonial?.result_metric || currentTestimonial?.result) && (
                        <div className="flex-shrink-0">
                          <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm shadow-sm border border-white/10 bg-white/5 ${currentStyle.icon}`}>
                            <Icon name="Award" size={16} />
                            {currentTestimonial?.result_metric || currentTestimonial?.result}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (currentTestimonial?.result_metric || currentTestimonial?.result) ? (
                    <div className="mb-6">
                      <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm shadow-sm border border-white/10 bg-white/5 ${currentStyle.icon}`}>
                        <Icon name="Award" size={16} />
                        {currentTestimonial?.result_metric || currentTestimonial?.result}
                      </span>
                    </div>
                  ) : null}
                </div>

                {/* Client Info */}
                <div className="flex items-center gap-4 mt-6 pt-6 border-t border-white/5">
                  <div className="w-16 h-16 rounded-full overflow-hidden shadow-card flex items-center justify-center bg-white p-1">
                    <Image
                      src={currentTestimonial?.image || currentTestimonial?.avatar}
                      alt={currentTestimonial?.name}
                      width={64}
                      height={64}
                      className="w-12 h-12 object-contain"
                    />                 
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-lg text-text-primary">
                      {currentTestimonial?.name}
                    </div>
                    <div className="text-text-secondary">
                      {currentTestimonial?.role || currentTestimonial?.position}
                    </div>
                    <div className="text-sm text-accent">
                      {currentTestimonial?.company}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            {/* Previous Button */}
            <button
              onClick={goToPrevious}
              className="w-12 h-12 rounded-full bg-white/5 hover:bg-accent text-text-primary hover:text-white transition-smooth flex items-center justify-center border border-white/10"
              aria-label="Previous testimonial"
            >
              <Icon name="ChevronLeft" size={20} />
            </button>

            {/* Dots Indicator */}
            <div className="flex items-center gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-smooth ${
                    index === currentIndex
                      ? 'bg-accent' :'bg-white/10 hover:bg-white/20'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            {/* Next Button */}
            <button
              onClick={goToNext}
              className="w-12 h-12 rounded-full bg-white/5 hover:bg-accent text-text-primary hover:text-white transition-smooth flex items-center justify-center border border-white/10"
              aria-label="Next testimonial"
            >
              <Icon name="ChevronRight" size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialCarousel;